import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CardComponent from "../../components/card/CardComponent";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import { Line } from "react-chartjs-2";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { getData } from "../../ServerRequest";
import SearchIcon from "@mui/icons-material/Search";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Dashboard() {
  const [buyers, setBuyers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        const [buyersRes, bookingsRes, wishlistsRes] = await Promise.all([
          getData("/api/buyer/"),
          getData("/api/buyer/booking/"),
          getData("/api/buyer/wishlist/"),
        ]);

        if (buyersRes.code === 200) {
          setBuyers(buyersRes.data.buyers);
        }
        if (bookingsRes.code === 200) {
          setBookings(bookingsRes.data.bookings);
        }
        if (wishlistsRes.code === 200) {
          setWishlists(wishlistsRes.data.wishlist);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Calculate metrics from bookings data
  const totalOrders = bookings.length;
  const pendingOrders = bookings.filter(
    (booking) => booking.ps_book_status === 0
  ).length;
  const totalCustomers = buyers.length;
  const prospects = new Set(wishlists.map((item) => item.buyer)).size;

  // Calculate current month's metrics
  const currentDate = new Date();
  const startOfCurrentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const startOfPreviousMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    1
  );

  // Current month's new entries
  const currentMonthNewOrders = bookings.filter((booking) => {
    const bookingDate = new Date(booking.ps_book_dt);
    return bookingDate >= startOfCurrentMonth;
  }).length;

  const currentMonthNewPendingOrders = bookings.filter((booking) => {
    const bookingDate = new Date(booking.ps_book_dt);
    return bookingDate >= startOfCurrentMonth && booking.ps_book_status === 0;
  }).length;

  const currentMonthNewCustomers = buyers.filter((buyer) => {
    const registerDate = new Date(buyer.ps_buyer_date);
    return registerDate >= startOfCurrentMonth;
  }).length;

  const currentMonthNewProspects = new Set(
    wishlists
      .filter((item) => {
        const wishlistDate = new Date(item.added_at);
        return wishlistDate >= startOfCurrentMonth;
      })
      .map((item) => item.buyer)
  ).size;

  // Previous month's new entries
  const previousMonthNewOrders = bookings.filter((booking) => {
    const bookingDate = new Date(booking.ps_book_dt);
    return (
      bookingDate >= startOfPreviousMonth && bookingDate < startOfCurrentMonth
    );
  }).length;

  const previousMonthNewPendingOrders = bookings.filter((booking) => {
    const bookingDate = new Date(booking.ps_book_dt);
    return (
      bookingDate >= startOfPreviousMonth &&
      bookingDate < startOfCurrentMonth &&
      booking.ps_book_status === 0
    );
  }).length;

  const previousMonthNewCustomers = buyers.filter((buyer) => {
    const registerDate = new Date(buyer.ps_buyer_date);
    return (
      registerDate >= startOfPreviousMonth && registerDate < startOfCurrentMonth
    );
  }).length;

  const previousMonthNewProspects = new Set(
    wishlists
      .filter((item) => {
        const wishlistDate = new Date(item.added_at);
        return (
          wishlistDate >= startOfPreviousMonth &&
          wishlistDate < startOfCurrentMonth
        );
      })
      .map((item) => item.buyer)
  ).size;

  // Debug logs
  console.log("Current Month Stats:", {
    orders: currentMonthNewOrders,
    pendingOrders: currentMonthNewPendingOrders,
    customers: currentMonthNewCustomers,
    prospects: currentMonthNewProspects,
  });

  console.log("Previous Month Stats:", {
    orders: previousMonthNewOrders,
    pendingOrders: previousMonthNewPendingOrders,
    customers: previousMonthNewCustomers,
    prospects: previousMonthNewProspects,
  });

  // Calculate percentage changes
  const calculatePercentage = (current, previous) => {
    console.log(
      `Calculating percentage - Current: ${current}, Previous: ${previous}`
    );
    if (previous === 0) {
      if (current === 0) return "0";
      return "+100";
    }
    const change = ((current - previous) / previous) * 100;
    console.log(`Raw change: ${change}`);
    // Cap the percentage at +/- 100
    const cappedChange = Math.max(Math.min(change, 100), -100);
    console.log(`Capped change: ${cappedChange}`);
    return cappedChange > 0
      ? `+${Math.round(cappedChange)}`
      : Math.round(cappedChange).toString();
  };

  const cardData = [
    {
      heading: "Total Orders",
      percentageV: calculatePercentage(
        currentMonthNewOrders,
        previousMonthNewOrders
      ),
      value: totalOrders.toString(),
      bgcolor: "#F3F4F6",
      icon: "orders",
    },
    {
      heading: "Pending Orders",
      percentageV: calculatePercentage(
        currentMonthNewPendingOrders,
        previousMonthNewPendingOrders
      ),
      value: pendingOrders.toString(),
      bgcolor: "#F3F4F6",
      icon: "orders",
    },
    {
      heading: "Total Customers",
      percentageV: calculatePercentage(
        currentMonthNewCustomers,
        previousMonthNewCustomers
      ),
      value: totalCustomers.toString(),
      bgcolor: "#F3F4F6",
      icon: "customers",
    },
    {
      heading: "Prospects",
      percentageV: calculatePercentage(
        currentMonthNewProspects,
        previousMonthNewProspects
      ),
      value: prospects.toString(),
      bgcolor: "#F3F4F6",
      icon: "customers",
    },
  ];

  // Get unique years from bookings data
  const getAvailableYears = () => {
    const years = new Set(
      bookings.map((booking) => new Date(booking.ps_book_dt).getFullYear())
    );
    return Array.from(years).sort((a, b) => b - a);
  };

  // Calculate month-wise distribution for selected year
  const getMonthWiseData = (year) => {
    const monthWiseData = bookings.reduce((acc, booking) => {
      const date = new Date(booking.ps_book_dt);
      if (date.getFullYear() === year) {
        const month = date.getMonth();
        const status = booking.ps_book_status === 1 ? "approved" : "pending";

        if (!acc[month]) {
          acc[month] = { approved: 0, pending: 0 };
        }
        acc[month][status]++;
      }
      return acc;
    }, {});

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const approvedData = months.map(
      (_, index) => monthWiseData[index]?.approved || 0
    );
    const pendingData = months.map(
      (_, index) => monthWiseData[index]?.pending || 0
    );

    return { approvedData, pendingData };
  };

  const { approvedData, pendingData } = getMonthWiseData(selectedYear);

  const lineChartData = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "Approved Orders",
        data: approvedData,
        borderColor: "#8884d8",
        backgroundColor: "rgba(136, 132, 216, 0.2)",
        fill: true,
      },
      {
        label: "Pending Orders",
        data: pendingData,
        borderColor: "#82ca9d",
        backgroundColor: "rgba(130, 202, 157, 0.2)",
        fill: true,
      },
    ],
  };

  // Calculate city-wise order distribution by joining bookings with buyers
  const cityOrderDistribution = bookings.reduce((acc, booking) => {
    const buyer = buyers.find((b) => b.ps_buyer === booking.ps_book_buyerid);
    const city = buyer?.ps_buyer_city || "Unknown";
    acc[city] = (acc[city] || 0) + 1;
    return acc;
  }, {});

  const pieChartData = {
    labels: Object.keys(cityOrderDistribution),
    datasets: [
      {
        data: Object.values(cityOrderDistribution),
        backgroundColor: [
          "#0088FE",
          "#00C49F",
          "#FFBB28",
          "#FF8042",
          "#8884d8",
          "#82ca9d",
        ],
      },
    ],
  };

  const columns = [
    { field: "ps_buyer", headerName: "Buyer ID", width: 150 },
    { field: "ps_buyer_name", headerName: "Name", width: 200 },
    { field: "ps_buyer_comp", headerName: "Company", width: 200 },
    { field: "ps_buyer_email", headerName: "Email", width: 250 },
    { field: "ps_buyer_city", headerName: "City", width: 150 },
    { field: "ps_buyer_country", headerName: "Country", width: 150 },
  ];

  // Filter buyers based on search query
  const filteredBuyers = buyers.filter((buyer) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      buyer.ps_buyer_name?.toLowerCase().includes(searchLower) ||
      buyer.ps_buyer_comp?.toLowerCase().includes(searchLower) ||
      buyer.ps_buyer_email?.toLowerCase().includes(searchLower) ||
      buyer.ps_buyer_city?.toLowerCase().includes(searchLower) ||
      buyer.ps_buyer_country?.toLowerCase().includes(searchLower) ||
      buyer.ps_buyer?.toString().includes(searchLower)
    );
  });

  return (
    <Container>
      <Box sx={{ marginLeft: 0 }}>
        <Grid container spacing={3} sx={{ fontFamily: "Inter, sans-serif" }}>
          {/* Information Cards */}
          {cardData.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <CardComponent
                key={index}
                heading={card.heading}
                percentageV={card.percentageV}
                value={card.value}
                bgcolor={card.bgcolor}
                icon={card.icon}
              />
            </Grid>
          ))}

          {/* Line Chart with Year Selection */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                boxShadow: 0,
                padding: 3,
                borderRadius: 0,
                backgroundColor: "#FAFAFA",
                border: "1px solid #BCC1CA",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
              marginBottom={2}
            >
              <Typography variant="h6" component="div" sx={{ marginBottom: 3 }}>
                Month wise orders
              </Typography>
              <FormControl sx={{ minWidth: 80, marginBottom: 2 }}>
                <InputLabel
                  id="year-select-label"
                  sx={{ fontSize: "0.875rem" }}
                >
                  Year
                </InputLabel>
                <Select
                  labelId="year-select-label"
                  value={selectedYear}
                  label="Year"
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{
                    backgroundColor: "white",
                    "& .MuiSelect-select": {
                      padding: "8px 14px",
                      fontSize: "0.875rem",
                    },
                  }}
                  size="small"
                >
                  {getAvailableYears().map((year) => (
                    <MenuItem
                      key={year}
                      value={year}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div style={{ width: "100%", height: 345 }}>
                <Line data={lineChartData} />
              </div>
            </Box>
          </Grid>

          {/* Pie Chart */}
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                boxShadow: 0,
                padding: 3,
                borderRadius: 0,
                backgroundColor: "#FAFAFA",
                border: "1px solid #BCC1CA",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
              marginBottom={2}
            >
              <Typography variant="h6" component="div" sx={{ marginBottom: 3 }}>
                Location wise orders
              </Typography>
              <div style={{ width: "80%", height: 400 }}>
                <Pie data={pieChartData} />
              </div>
            </Box>
          </Grid>

          {/* Table */}
          <Grid item xs={12}>
            <Box
              sx={{
                boxShadow: 0,
                padding: 2,
                borderRadius: 0,
                backgroundColor: "#FAFAFA",
                border: "1px solid #BCC1CA",
              }}
              marginBottom={2}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 2,
                }}
              >
                <Typography variant="h6" component="div">
                  Recent Customers
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    width: "300px",
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <div style={{ height: 400, width: "100%" }}>
                <DataGrid
                  rows={filteredBuyers}
                  columns={columns}
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                  loading={loading}
                  getRowId={(row) => row.ps_buyer}
                  sx={{
                    borderRadius: 0,
                    border: "1px solid #BCC1CA",
                    "& .MuiDataGrid-cell": { padding: "8px" },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#F3F4F6",
                      fontWeight: "bold",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      backgroundColor: "#F3F4F6",
                    },
                  }}
                />
              </div>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default Dashboard;
